import { useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { slugify } from "@/lib/slug";
import { toast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type Category = Database["public"]["Enums"]["post_category"];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}

const NewPostDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<Category>("article");
  const [submitting, setSubmitting] = useState(false);

  // Reset state every time the dialog opens.
  useEffect(() => {
    if (open) {
      setTitle("");
      setCategory("article");
      setSubmitting(false);
    }
  }, [open]);

  const create = async () => {
    const trimmed = title.trim();
    if (!trimmed) {
      toast({ title: "Title required", variant: "destructive" });
      return;
    }
    setSubmitting(true);

    // Derive an initial slug from the title, with a short random suffix
    // to avoid colliding with existing posts that share a title.
    const base = slugify(trimmed) || "post";
    const suffix = Math.random().toString(36).slice(2, 6);
    const slug = `${base}-${suffix}`;

    const { data, error } = await supabase
      .from("posts")
      .insert({
        title: trimmed,
        slug,
        excerpt: "",
        body_html: "",
        category,
      })
      .select("id")
      .single();
    setSubmitting(false);

    if (error || !data) {
      toast({
        title: "Could not create post",
        description: error?.message,
        variant: "destructive",
      });
      return;
    }
    onCreated(data.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New post</DialogTitle>
          <DialogDescription>
            Give your post a working title and pick a category. You can change everything later.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-post-title">Title</Label>
            <Input
              id="new-post-title"
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. AI trends to watch in 2026"
              onKeyDown={(e) => {
                if (e.key === "Enter" && title.trim()) {
                  e.preventDefault();
                  create();
                }
              }}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="new-post-category">Category</Label>
            <Select value={category} onValueChange={(v) => setCategory(v as Category)}>
              <SelectTrigger id="new-post-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="article">Article</SelectItem>
                <SelectItem value="insight">Insight</SelectItem>
                <SelectItem value="event">Event</SelectItem>
                <SelectItem value="case_study">Case study</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={create} disabled={submitting || !title.trim()}>
            {submitting ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NewPostDialog;
